import { AnyAction, Middleware } from 'redux';
import { v4 as uuidv4 } from 'uuid';
import { DUMMY_RESTAURANT_CODE } from '../constants';
import {
  AgentTypes,
  EventTypes,
  TTS_PRIORITY_BY_INTENT,
  TTS_PRIORITY_DEFAULT,
} from '../constants/event';
import {
  AudioFrameTransmissionMessage,
  getSessionBufferMessages,
  InfoTransmissionMessage,
  messagingActions,
  sendNetworkCallLogs,
  setEventWSConnectionDuration,
  setAudioWSConnectionDuration,
} from '../reducers/messagingSlice';
import { handleUpsellAttemptMetricOnTTSAcknowledged } from '../reducers/messagingSlice.utils';
import { checkHealthStatus } from '../redux/features/healthStatusCheck/healthStatusCheck.slice';
import { readEnvVariable } from '../utils';
import { agent } from '../utils/agent';
import { Counter } from '../utils/counter';
import logger from '../utils/logger';
import { getWebsocketUrl, sleep } from '../utils/network';
import { PerfTimer } from '../utils/timer';
import { RootStore } from './store';
import { CriticalChecksNames } from '../redux/features/healthScore/healthScore.constants';
import { updateCriticalCheckScore } from '../redux/features/healthScore/healthScore.slice';
import { AIStatusColors } from '../redux/features/ai/ai.constants';
import { WebSocketCloseCode } from '../constants/enums';
import { buildAndDispatchOrderProcessingMetric } from '../reducers/orderSlice.utils';

const isMatchMessagingAction = (action: AnyAction) => {
  const {
    sendMessage,
    sendInfo,
    sendOrder,
    sendAgentCart,
    sendLoyalty,
    sendEndSession,
    sendTTSRequest,
    sendError,
    sendAgentFirstActivity,
    sendAgentFirstInterception,
    sendAgentInterception,
    sendAgentMetric,
    sendStaffInterception,
    sendHITLSessionEnd,
    sendHITLSessionStart,
    sendHITLEventConnection,
    sendCancelOrder,
  } = messagingActions;
  return (
    sendMessage.match(action) ||
    sendInfo.match(action) ||
    sendOrder.match(action) ||
    sendAgentCart.match(action) ||
    sendLoyalty.match(action) ||
    sendEndSession.match(action) ||
    sendTTSRequest.match(action) ||
    sendError.match(action) ||
    sendAgentFirstActivity.match(action) ||
    sendAgentFirstInterception.match(action) ||
    sendAgentInterception.match(action) ||
    sendAgentMetric.match(action) ||
    sendStaffInterception.match(action) ||
    sendHITLSessionEnd.match(action) ||
    sendHITLSessionStart.match(action) ||
    sendHITLEventConnection.match(action) ||
    sendCancelOrder.match(action)
  );
};

const socketMiddleware: Middleware = (store: RootStore) => {
  let eventSocket: WebSocket | undefined = undefined; // Websocket of event backend
  let audioSocket: WebSocket | undefined = undefined; // Websocket of audio backend

  let eventWebSocketConnectionTimer = new PerfTimer({ autoStart: false });
  let audioWebSocketConnectionTimer = new PerfTimer({ autoStart: false });

  let audioWebSocketRetries = 1; // Count of event backend websocket retry
  let eventWebSocketRetries = 1; // Count of audio backend websocket retry

  // Timer cleanup references for event websocket
  let eventMessageInterval: NodeJS.Timeout | null = null;
  let eventAudioSampleInterval: NodeJS.Timeout | null = null;
  let eventHeartbeatInterval: NodeJS.Timeout | null = null;

  // Timer cleanup references for audio websocket
  let audioMessageInterval: NodeJS.Timeout | null = null;
  let audioAudioSampleInterval: NodeJS.Timeout | null = null;
  let audioHeartbeatInterval: NodeJS.Timeout | null = null;

  const HEARTBEAT_INTERVAL = 10000; // Send ping every 10 seconds

  // Helper function to clean up event websocket intervals
  const clearEventWebSocketIntervals = () => {
    if (eventMessageInterval) {
      clearInterval(eventMessageInterval);
      eventMessageInterval = null;
    }
    if (eventAudioSampleInterval) {
      clearInterval(eventAudioSampleInterval);
      eventAudioSampleInterval = null;
    }
    if (eventHeartbeatInterval) {
      clearInterval(eventHeartbeatInterval);
      eventHeartbeatInterval = null;
    }
  };

  // Helper function to clean up audio websocket intervals
  const clearAudioWebSocketIntervals = () => {
    if (audioMessageInterval) {
      clearInterval(audioMessageInterval);
      audioMessageInterval = null;
    }
    if (audioAudioSampleInterval) {
      clearInterval(audioAudioSampleInterval);
      audioAudioSampleInterval = null;
    }
    if (audioHeartbeatInterval) {
      clearInterval(audioHeartbeatInterval);
      audioHeartbeatInterval = null;
    }
  };

  // Helper function to setup heartbeat interval
  const setupHeartbeat = (
    selectedRestaurantCode: string,
    isAudioWS: boolean,
    socket: WebSocket | undefined
  ) => {
    const interval = setInterval(() => {
      if (socket?.readyState === WebSocket.OPEN) {
        const { sessionBoundary, messages, taskRouter, user } =
          store.getState() || {};
        const { email, username, id, firstName, lastName } =
          user?.userProfile || {};
        try {
          const pingPayload = JSON.stringify({
            type: 'ping',
            id: uuidv4(),
            ...(id && {
              metadata: {
                agent_name: username,
                agent_id: id,
                agent_email: email,
                agent_first_name: firstName,
                agent_last_name: lastName,
              },
            }),
            session_id:
              taskRouter?.taskAssignedSessionId ||
              sessionBoundary?.sessionId ||
              sessionBoundary?.endedSessionId ||
              messages?.currentSessionId,
          });
          socket.send(pingPayload);
        } catch (error) {
          logger.log({
            restaurantCode: selectedRestaurantCode,
            message: `${
              isAudioWS ? 'Audio' : 'Event'
            } websocket ping failed to send`,
            moreInfo: JSON.stringify(error, Object.getOwnPropertyNames(error)),
          });
        }
      }
    }, HEARTBEAT_INTERVAL);

    if (isAudioWS) {
      audioHeartbeatInterval = interval;
    } else {
      eventHeartbeatInterval = interval;
    }
  };

  const shouldRetryConnection = function (event: any) {
    return (
      event.code !== WebSocketCloseCode.NORMAL_CLOSURE &&
      event.code !== WebSocketCloseCode.GOING_AWAY
    );
  };

  const handleWebSocketClose = ({
    event,
    isAudioWS,
    selectedRestaurantCode,
    onConnectionLost,
    onCloseConnection,
    retryConnection,
  }: {
    event: any;
    isAudioWS: boolean;
    selectedRestaurantCode: string;
    onConnectionLost: () => void;
    onCloseConnection: () => void;
    retryConnection: (params: {
      isAudioWS: boolean;
      maxRetries?: number;
    }) => void;
  }) => {
    const wsType = isAudioWS ? 'Audio' : 'Event';

    if (shouldRetryConnection(event)) {
      logger.log({
        restaurantCode: selectedRestaurantCode,
        message: `${wsType} websocket closed abnormally with code ${event.code}, attempting reconnect`,
      });
      onConnectionLost();
      retryConnection({ isAudioWS, maxRetries: 5 });
    } else {
      logger.log({
        restaurantCode: selectedRestaurantCode,
        message: `${wsType} websocket closed gracefully with code ${event.code}, not reconnecting`,
      });
      onCloseConnection();
    }
  };

  return (next) => (action) => {
    const {
      restaurant: {
        selectedRestaurantDetails: { restaurantCode: selectedRestaurantCode },
        selectedStage,
      },
      config: { WEBSOCKET_RETRY_MAX_BACKOFF },
      messages: { isConnected },
      user,
      config,
      taskRouter: { taskAssignedSessionId },
    } = store.getState();

    const isEventConnectionEstablished = eventSocket && isConnected;

    const reconnect = async ({
      isAudioWS,
      maxRetries = 9,
    }: {
      isAudioWS: boolean;
      maxRetries?: number;
    }) => {
      if (!store.getState()?.user?.isLoggedIn) {
        return;
      }
      let retries = isAudioWS ? audioWebSocketRetries : eventWebSocketRetries;
      if (retries < maxRetries) {
        let multiple = 1 + Math.random(); // multiple is in the range 1+[0 to 1] = [1 to 2]
        let timeToWait = 2 ** retries * 1000 * multiple;
        await sleep(
          timeToWait > WEBSOCKET_RETRY_MAX_BACKOFF
            ? WEBSOCKET_RETRY_MAX_BACKOFF
            : timeToWait
        );

        const isWSEstablished = isAudioWS
          ? audioSocket && audioSocket.readyState !== audioSocket.CLOSED
          : eventSocket && eventSocket.readyState !== eventSocket.CLOSED;

        if (isWSEstablished || !store.getState()?.user?.isLoggedIn) {
          return;
        }
        // reconnect if lost connection
        logger.log({
          restaurantCode: selectedRestaurantCode,
          message: `In ${retries} retry to established connection to restaurant specific ${
            isAudioWS ? 'audio' : ''
          } websocket`,
        });

        if (isAudioWS) {
          store.dispatch(
            messagingActions.setAudioWSConnectionMessage(
              `Reconnecting audio websocket... (Attempt ${audioWebSocketRetries}/${maxRetries})`
            )
          );
          audioWebSocketRetries += 1;
          audioConnect();
        } else {
          store.dispatch(
            messagingActions.setEventWSConnectionMessage(
              `Reconnecting event websocket... (Attempt ${eventWebSocketRetries}/${maxRetries})`
            )
          );
          eventWebSocketRetries += 1;
          eventConnect();
        }
      } else {
        logger.log({
          restaurantCode: selectedRestaurantCode,
          message: `Reached  maximum retry of ${retries} to established connection to restaurant specific ${
            isAudioWS ? 'audio' : ''
          } websocket`,
        });

        // Notify user that max retries reached
        if (isAudioWS) {
          store.dispatch(
            messagingActions.setAudioWSConnectionMessage(
              `Failed to reconnect audio websocket after ${maxRetries} attempts. Please check your connection or refresh browser`
            )
          );
        } else {
          store.dispatch(
            messagingActions.setEventWSConnectionMessage(
              `Failed to reconnect event websocket after ${maxRetries} attempts. Please check your connection or or refresh browser`
            )
          );
        }

        store.dispatch(
          updateCriticalCheckScore({
            name: isAudioWS
              ? CriticalChecksNames.wsRestaurantAudio
              : CriticalChecksNames.wsRestaurantEvents,
            value: 0,
          })
        );
      }
    };

    const eventConnect = () => {
      const username = user?.userProfile?.username;
      if (!selectedRestaurantCode || !username) {
        return;
      }
      agent.setAgentId(username);

      if (eventSocket) {
        eventSocket.close();
      }

      clearEventWebSocketIntervals();

      const eventWebSocketURL = getWebsocketUrl(
        config,
        selectedRestaurantCode,
        selectedStage,
        false
      );

      eventWebSocketConnectionTimer.start();
      eventSocket = new WebSocket(eventWebSocketURL);

      logger.log({
        restaurantCode: selectedRestaurantCode,
        message:
          'Trying to establish connection to restaurant specific websocket',
      });

      eventSocket.onerror = (error: Event) => {
        logger.log({
          restaurantCode: selectedRestaurantCode,
          message: 'Restaurant specific websocket encountered an error',
          moreInfo: JSON.stringify(error, Object.getOwnPropertyNames(error)),
        });
      };

      eventSocket.onopen = () => {
        logger.log({
          restaurantCode: selectedRestaurantCode,
          message:
            'Successfully established connection to restaurant specific websocket',
        });

        const eventConnectionDuration = eventWebSocketConnectionTimer.stop();
        store.dispatch(messagingActions.connectionEstablished());
        store.dispatch(setEventWSConnectionDuration(eventConnectionDuration));
        setupHeartbeat(selectedRestaurantCode, false, eventSocket);
        if (eventWebSocketRetries > 1) {
          store.dispatch(
            messagingActions.setEventWSConnectionMessage(
              'Event websocket reconnected successfully'
            )
          );
          store.dispatch(messagingActions.setEventWSConnectionSuccess(true));
        }
        eventWebSocketRetries = 1;

        const socketUrl = eventWebSocketURL.substring(
          0,
          eventWebSocketURL.indexOf('HITL_100') + 'HITL_100'.length
        );
        store.dispatch(
          sendNetworkCallLogs({
            url: socketUrl,
            duration: eventConnectionDuration,
            message:
              'restaurant specific websocket connection established successfully',
            via: 'websocket',
          })
        );

        const payload: Partial<InfoTransmissionMessage> = {
          data: { type: 'METRIC', message: 'Test event websocket signal' },
        };
        store.dispatch(messagingActions.sendInfo(payload as any));
        store.dispatch(
          updateCriticalCheckScore({
            name: CriticalChecksNames.wsRestaurantEvents,
            value: 1,
          })
        );

        const {
          taskRouter: { isCurrentTaskUnsupervised, currentTaskId },
          ai: { isAIAutoMode, aiStatus },
        } = store.getState();
        if (
          currentTaskId &&
          !isCurrentTaskUnsupervised &&
          isAIAutoMode &&
          aiStatus.status === AIStatusColors.green
        ) {
          // Check AI messages in case event websocket is not established before AI escalation on car enter on TR supervised AI mode
          logger.log({
            restaurantCode: selectedRestaurantCode,
            message: 'Check AI messages when event socket is open',
          });
          store.dispatch(getSessionBufferMessages({ checkAIMessages: true }));
        }
      };

      let messages: any[] = [];
      let audioMessageSample: AudioFrameTransmissionMessage | null = null;
      eventSocket.onmessage = (event: any) => {
        const data = JSON.parse(event.data);
        if (data) {
          messages.push(data);
        }
      };

      eventMessageInterval = setInterval(() => {
        if (messages.length) {
          const filteredMessages: any[] = [],
            nonAudioMessages: any[] = [];

          messages.forEach((message) => {
            if (
              !taskAssignedSessionId ||
              (taskAssignedSessionId &&
                message.session_id === taskAssignedSessionId)
            ) {
              filteredMessages.push(message);

              // Handle upsell metric for ORDERBOARD text events when TTS is acknowledged.
              if (message.event === EventTypes.text) {
                handleUpsellAttemptMetricOnTTSAcknowledged(
                  message,
                  store.dispatch as any
                );
              }

              if (message.event !== EventTypes.audio) {
                nonAudioMessages.push(message);
              } else {
                if (!audioMessageSample) audioMessageSample = message;
              }
            }
          });

          if (nonAudioMessages.length) {
            logger.log({
              restaurantCode: selectedRestaurantCode,
              message:
                'Process non-audio message via restaurant specific websocket',
              moreInfo: JSON.stringify(nonAudioMessages),
            });
          }

          const {
            taskRouter,
            sessionBoundary,
            ai,
            config: {
              FEATURE_FLAG_FINAL_ORDER_VALIDATION,
              FEATURE_FLAG_LOYALTY_SUPPORT,
            },
          } = store.getState();
          store.dispatch(
            messagingActions.messageReceived({
              messages: filteredMessages,
              taskRouter,
              sessionBoundarySessionId: sessionBoundary.sessionId,
              ai,
              FEATURE_FLAG_FINAL_ORDER_VALIDATION,
              FEATURE_FLAG_LOYALTY_SUPPORT,
            })
          );
        }
        messages = [];
      }, 100);

      // Sampling audio event every 10 second
      eventAudioSampleInterval = setInterval(() => {
        if (audioMessageSample) {
          logger.log({
            restaurantCode: selectedRestaurantCode,
            message: `Process audio message via restaurant specific websocket`,
            moreInfo: JSON.stringify(audioMessageSample),
          });
          audioMessageSample = null;
        }
      }, 10000);

      eventSocket.onclose = (event: any) => {
        logger.log({
          restaurantCode: selectedRestaurantCode,
          message: 'Restaurant specific websocket is closed',
          moreInfo: JSON.stringify(event, ['code', 'reason', 'wasClean']),
        });
        if (eventSocket?.readyState === eventSocket?.CLOSED) {
          eventWebSocketConnectionTimer.stop();
        }
        // Clean up intervals on close
        clearEventWebSocketIntervals();

        handleWebSocketClose({
          event,
          isAudioWS: false,
          selectedRestaurantCode,
          onConnectionLost: () =>
            store.dispatch(messagingActions.connectionLost()),
          onCloseConnection: () =>
            store.dispatch(messagingActions.closeConnection()),
          retryConnection: reconnect,
        });
      };

      store.dispatch(checkHealthStatus());
    };

    const audioConnect = () => {
      const username = user?.userProfile?.username;

      if (!selectedRestaurantCode || !username) {
        return;
      }

      agent.setAgentId(username);

      if (audioSocket) {
        audioSocket.close();
      }

      clearAudioWebSocketIntervals();

      const audioWebSocketURL = getWebsocketUrl(
        config,
        selectedRestaurantCode,
        selectedStage,
        true
      );

      audioWebSocketConnectionTimer.start();
      audioSocket = new WebSocket(audioWebSocketURL);

      logger.log({
        restaurantCode: selectedRestaurantCode,
        message: `Trying to establish connection to restaurant specific audio websocket`,
      });

      audioSocket.onerror = (error: Event) => {
        logger.log({
          restaurantCode: selectedRestaurantCode,
          message: 'Restaurant specific audio websocket encountered an error',
          moreInfo: JSON.stringify(error, Object.getOwnPropertyNames(error)),
        });
      };

      audioSocket.onopen = () => {
        logger.log({
          restaurantCode: selectedRestaurantCode,
          message: `Successfully established connection to restaurant specific audio websocket`,
        });

        const audioConnectionDuration = audioWebSocketConnectionTimer.stop();
        store.dispatch(messagingActions.audioWSConnectionEstablished());
        store.dispatch(setAudioWSConnectionDuration(audioConnectionDuration));
        setupHeartbeat(selectedRestaurantCode, true, audioSocket);
        // Show success message if reconnection succeeded after retries
        if (audioWebSocketRetries > 1) {
          store.dispatch(
            messagingActions.setAudioWSConnectionMessage(
              'Audio websocket reconnected successfully'
            )
          );
          store.dispatch(messagingActions.setAudioWSConnectionSuccess(true));
        }

        audioWebSocketRetries = 1;
        const audioUrl = audioWebSocketURL.substring(
          0,
          audioWebSocketURL.indexOf('HITL_100') + 'HITL_100'.length
        );
        store.dispatch(
          sendNetworkCallLogs({
            url: audioUrl,
            duration: audioConnectionDuration,
            message: `Restaurant specific audio websocket connection established successfully`,
            via: 'websocket',
          })
        );

        const payload: Partial<InfoTransmissionMessage> = {
          data: {
            type: 'METRIC',
            message: `Test audio websocket signal`,
          },
        };
        store.dispatch(messagingActions.sendInfo(payload as any));
        store.dispatch(
          updateCriticalCheckScore({
            name: CriticalChecksNames.wsRestaurantAudio,
            value: 1,
          })
        );
      };

      let messages: any[] = [];
      let audioMessageSample: AudioFrameTransmissionMessage | null = null;
      audioSocket.onmessage = (event: any) => {
        const data = JSON.parse(event.data);
        if (data) {
          messages.push(data);
        }
      };

      audioMessageInterval = setInterval(() => {
        if (messages.length) {
          const filteredMessages: any[] = [],
            nonAudioMessages: any[] = [];

          messages.forEach((message) => {
            if (
              !taskAssignedSessionId ||
              (taskAssignedSessionId &&
                message.session_id === taskAssignedSessionId)
            ) {
              filteredMessages.push(message);
              if (message.event !== EventTypes.audio) {
                nonAudioMessages.push(message);
              } else {
                if (!audioMessageSample) audioMessageSample = message;
              }
            }
          });

          const {
            taskRouter,
            sessionBoundary,
            ai,
            config: {
              FEATURE_FLAG_FINAL_ORDER_VALIDATION,
              FEATURE_FLAG_LOYALTY_SUPPORT,
            },
          } = store.getState();
          store.dispatch(
            messagingActions.messageReceived({
              messages: filteredMessages,
              taskRouter,
              sessionBoundarySessionId: sessionBoundary.sessionId,
              ai,
              FEATURE_FLAG_FINAL_ORDER_VALIDATION,
              FEATURE_FLAG_LOYALTY_SUPPORT,
            })
          );
        }
        messages = [];
      }, 100);

      // Sampling audio event every 10 second
      audioAudioSampleInterval = setInterval(() => {
        if (audioMessageSample) {
          logger.log({
            restaurantCode: selectedRestaurantCode,
            message: `Process audio message via restaurant specific websocket`,
            moreInfo: JSON.stringify(audioMessageSample),
          });
          audioMessageSample = null;
        }
      }, 10000);

      audioSocket.onclose = (event: any) => {
        clearAudioWebSocketIntervals();
        logger.log({
          restaurantCode: selectedRestaurantCode,
          message: `Restaurant specific audio websocket is closed`,
          moreInfo: JSON.stringify(event, ['code', 'reason', 'wasClean']),
        });

        if (audioSocket?.readyState === audioSocket?.CLOSED) {
          audioWebSocketConnectionTimer.stop();
        }

        handleWebSocketClose({
          event,
          isAudioWS: true,
          selectedRestaurantCode,
          onConnectionLost: () =>
            store.dispatch(messagingActions.audioWSConnectionLost()),
          onCloseConnection: () =>
            store.dispatch(messagingActions.closeAudioWSConnection()),
          retryConnection: reconnect,
        });
      };
    };

    if (messagingActions.startConnecting.match(action)) {
      eventConnect();
    }

    if (messagingActions.closeConnection.match(action)) {
      if (eventSocket) {
        // Clean up intervals before closing
        clearEventWebSocketIntervals();
        // Set onclose handler before closing to prevent reconnection attempts
        eventSocket.onclose = () => {
          logger.log({
            restaurantCode: selectedRestaurantCode,
            message:
              'Successfully closed connection to restaurant specific websocket by close-connection action',
          });
        };
        eventSocket.close();
        eventSocket = undefined;
        eventWebSocketRetries = 1;
      }
    }

    if (messagingActions.startAudioWSConnecting.match(action)) {
      audioConnect();
    }

    if (messagingActions.closeAudioWSConnection.match(action)) {
      if (audioSocket) {
        // Clean up intervals before closing
        clearAudioWebSocketIntervals();
        // Set onclose handler before closing to prevent reconnection attempts
        audioSocket.onclose = () => {
          logger.log({
            restaurantCode: selectedRestaurantCode,
            message:
              'Successfully closed connection to restaurant specific audio websocket by close-connection action',
          });
        };
        audioSocket.close();
        audioSocket = undefined;
        audioWebSocketRetries = 1;
      }
    }

    if (isEventConnectionEstablished && isMatchMessagingAction(action)) {
      eventSocket?.send(JSON.stringify(action.payload));
    }

    next(action);

    if (messagingActions.messageReceived.match(action)) {
      action.payload.messages.forEach((message: any) => {
        if (message.event === EventTypes.check) {
          const requestId = message.data?.request_id;
          if (requestId) {
            buildAndDispatchOrderProcessingMetric(
              store.getState().order,
              requestId,
              store.dispatch
            );
          }
        }
      });
    }
  };
};

export let infoSeqIdCounter = new Counter();
export let hitlSessionStartSeqIdCounter = new Counter();

const messagingTransformMiddleware: Middleware = (store: RootStore) => {
  let textSeqIdCounter = 0;
  let endSessionSeqIdCounter = 0;
  let orderSeqIdCounter = 0;
  let agentCartSeqIdCounter = 0;
  let loyaltySeqIdCounter = 0;
  let ttsRequestSeqIdCounter = 0;
  let errorSeqIdCounter = 0;
  let agentFirstActivitySeqIdCounter = 0;
  let agentFirstInterceptionSeqIdCounter = 0;
  let agentInterceptionSeqIdCounter = 0;
  let agentMetricSeqIdCounter = 0;
  let staffInterceptionSeqIdCounter = 0;
  let hitlSessionEndSeqIdCounter = 0;
  let hitlEventConnectionSeqIdCounter = 0;
  let cancelOrderSeqIdCounter = 0;

  return (next) => (action) => {
    if (isMatchMessagingAction(action)) {
      const { sessionBoundary, user, messages, restaurant, taskRouter, menu } =
        store.getState() || {};
      const { email, username, id, firstName, lastName } =
        user?.userProfile || {};

      let payload: any = {
        ...action.payload,
        id: uuidv4(),
        session_id:
          taskRouter?.taskAssignedSessionId ||
          sessionBoundary?.sessionId ||
          sessionBoundary?.endedSessionId ||
          messages?.currentSessionId,
        ...(taskRouter.taskAssignedLaneId
          ? { lane_id: taskRouter.taskAssignedLaneId }
          : null),
        agent_type: AgentTypes.HITL,
        agent_id: agent.getAgentId(),
        timestamp: new Date().toISOString(),
        restaurant_code:
          restaurant.selectedRestaurantDetails?.restaurantCode ||
          DUMMY_RESTAURANT_CODE,
        // I would prefer not to expose this, see PRV-2631
        metadata: {
          agent_name: username,
          agent_id: id,
          agent_email: email,
          agent_first_name: firstName,
          agent_last_name: lastName,
        },
        environment: readEnvVariable('DEPLOY_ENV'),
      };

      console.time('middleware-action-switch');
      switch (action.type) {
        case messagingActions.sendMessage.type:
          console.time('middleware-sendMessage');
          payload.event = EventTypes.text;
          payload.seq = textSeqIdCounter++;
          // Add TTS priority to metadata based on intent
          const intent = action.payload?.data?.metadata?.intent;
          const priority = intent
            ? TTS_PRIORITY_BY_INTENT[intent] ?? TTS_PRIORITY_DEFAULT
            : TTS_PRIORITY_DEFAULT;
          payload.metadata = {
            ...payload.metadata,
            priority,
          };
          console.timeEnd('middleware-sendMessage');
          break;
        case messagingActions.sendEndSession.type:
          payload.event = EventTypes.endSession;
          payload.seq = endSessionSeqIdCounter++;
          break;
        case messagingActions.sendInfo.type:
          if (
            payload.data &&
            'new_language' in payload.data &&
            !payload.data.type
          ) {
            // Transform to language switch format
            payload.event = EventTypes.languageSwitch;
            payload.environment = readEnvVariable('DEPLOY_ENV') || 'PRODUCTION';
            payload.metadata = {}; // Empty metadata as per schema
          } else {
            payload.event = EventTypes.info;
          }
          payload.seq = infoSeqIdCounter.increment();
          break;
        case messagingActions.sendOrder.type:
          const { loadedVersionDetails } = menu || {};
          const {
            isBrandMenu,
            prpMenuVersion,
            prpMenuTitle,
            brandMenuVersion,
          } = loadedVersionDetails || {};

          payload.event = EventTypes.order;
          payload.seq = orderSeqIdCounter++;
          payload.metadata = {
            ...payload.metadata,
            ...(isBrandMenu
              ? {
                  brand_menu_version: brandMenuVersion + '',
                }
              : prpMenuVersion
              ? {
                  prp_menu_version: prpMenuVersion + '',
                  prp_menu_title: prpMenuTitle,
                }
              : null),
          };
          break;
        case messagingActions.sendAgentCart.type:
          payload.event = EventTypes.agentCart;
          payload.seq = agentCartSeqIdCounter++;
          break;
        case messagingActions.sendAgentFirstActivity.type:
          payload.event = EventTypes.agentFirstActivity;
          payload.seq = agentFirstActivitySeqIdCounter++;
          break;
        case messagingActions.sendAgentFirstInterception.type:
          payload.event = EventTypes.agentFirstInterception;
          payload.seq = agentFirstInterceptionSeqIdCounter++;
          break;
        case messagingActions.sendAgentInterception.type:
          payload.event = EventTypes.agentInterception;
          payload.seq = agentInterceptionSeqIdCounter++;
          break;
        case messagingActions.sendAgentMetric.type:
          payload.event = EventTypes.agentMetric;
          payload.seq = agentMetricSeqIdCounter++;
          break;
        case messagingActions.sendStaffInterception.type:
          payload.event = EventTypes.staffIntervention;
          payload.seq = staffInterceptionSeqIdCounter++;
          break;
        case messagingActions.sendLoyalty.type:
          payload.event = EventTypes.loyalty;
          payload.seq = loyaltySeqIdCounter++;
          break;
        case messagingActions.sendTTSRequest.type:
          payload.event = EventTypes.TTSRequest;
          payload.seq = ttsRequestSeqIdCounter++;
          break;
        case messagingActions.sendError.type:
          payload.event = EventTypes.error;
          payload.seq = errorSeqIdCounter++;
          break;
        case messagingActions.sendHITLSessionStart.type:
          payload.event = EventTypes.hitlSessionStart;
          payload.seq = hitlSessionStartSeqIdCounter.increment();
          break;
        case messagingActions.sendHITLSessionEnd.type:
          payload.event = EventTypes.hitlSessionEnd;
          payload.seq = hitlSessionEndSeqIdCounter++;
          break;
        case messagingActions.sendHITLEventConnection.type:
          payload.event = EventTypes.hitlEventConnection;
          payload.seq = hitlEventConnectionSeqIdCounter++;
          break;
        case messagingActions.sendCancelOrder.type:
          payload.event = EventTypes.cancelOrder;
          payload.seq = cancelOrderSeqIdCounter++;
          break;
        default:
          break;
      }
      console.timeEnd('middleware-action-switch');
      if (!('data' in payload)) {
        payload.data = {}; // Sending empty data field to be consistent with the high level schema for all other events
      }
      action.payload = payload;
      logger.log({
        restaurantCode:
          store.getState().restaurant?.selectedRestaurantDetails.restaurantCode,
        message: `Sending the message with event ${payload.event} via restaurant specific websocket`,
        moreInfo: JSON.stringify(payload),
      });
    }

    next(action);
  };
};

export { messagingTransformMiddleware, socketMiddleware };
